import { useCallback, useEffect, useState } from 'react'
import ManageImages from 'views/ManageImages'
import UploadImages from 'views/UploadImages'
import ManageTags from 'views/ManageTags'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'
import { StyleUtil } from '../utils/StyleUtil'

enum Section {
  None = 0,
  Search = 1,
  Upload = 2,
  ManageImages = 3,
  ManageTags = 4,
}

export default function ImagesSection() {
  const [section, setSection] = useState<Section>(Section.None)
  const { getRole } = useCompositeAuth()
  const role = getRole()

  const canSearchImages =
    role === Role.SuperAdmin ||
    role === Role.Admin ||
    role === Role.Author ||
    role === Role.Designer ||
    role === Role.Contractor

  const canUploadImages =
    role === Role.SuperAdmin ||
    role === Role.Admin ||
    role === Role.Author ||
    role === Role.Designer ||
    role === Role.Contractor

  const canManageImages =
    role === Role.SuperAdmin || role === Role.Admin || role === Role.Designer

  const showSectionButtons =
    canSearchImages && (canUploadImages || canManageImages)

  const isActiveSection = (targetSection: Section) => {
    let className =
      section === targetSection
        ? StyleUtil.buttonPrimaryUnsw
        : StyleUtil.buttonBlackWhite
    // className = `${className} mentem-menu-button btn rounded-3xl hover:text-white hover:bg-primary hover:border-primary normal-case ml-6 mr-6 mt-2 mb-2`
    return className
  }

  const switchSection = (section: Section) => {
    setSection(section)
  }

  const onSelectSearchImages = () => {
    switchSection(Section.Search)
  }

  const onSelectUploadImages = () => {
    switchSection(Section.Upload)
  }

  const onSelectManageImages = () => {
    switchSection(Section.ManageImages)
  }

  const onSelectManageTags = () => {
    switchSection(Section.ManageTags)
  }

  const canAccessImagesSection = useCallback(
    (): Boolean => canSearchImages,
    [canSearchImages],
  )

  useEffect(() => {
    // for author default to search
    setSection(canAccessImagesSection() ? Section.Search : Section.None)
  }, [canAccessImagesSection])

  return (
    <>
      {showSectionButtons && (
        <div className="mt-4 flex flex-row justify-center gap-12">
          {canSearchImages && (
            <>
              <button
                data-testid="btn-search-images"
                className={isActiveSection(Section.Search)}
                onClick={onSelectSearchImages}
              >
                <span className="pl-1 pr-1">Images</span>
              </button>
              {canUploadImages && (
                <>
                  <button
                    data-testid="btn-upload-images"
                    className={isActiveSection(Section.Upload)}
                    onClick={onSelectUploadImages}
                  >
                    <span className="pl-1 pr-1">Upload</span>
                  </button>
                </>
              )}
              {canManageImages && (
                <>
                  <button
                    data-testid="btn-manage-images"
                    className={isActiveSection(Section.ManageImages)}
                    onClick={onSelectManageImages}
                  >
                    <span className="pl-1 pr-1">Manage images</span>
                  </button>
                  <button
                    data-testid="btn-manage-tags"
                    className={isActiveSection(Section.ManageTags)}
                    onClick={onSelectManageTags}
                  >
                    <span className="pl-1 pr-1">Manage subjects</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {canSearchImages && section === Section.Search && (
        <ManageImages mode="SEARCH" />
      )}
      {canUploadImages && section === Section.Upload && <UploadImages />}
      {canManageImages && section === Section.ManageImages && (
        <ManageImages mode="MANAGE" />
      )}
      {canManageImages && section === Section.ManageTags && <ManageTags />}
    </>
  )
}
